import { useState } from "react";

import { DataTable, type DataTableColumn } from "@/components/cinch/DataTable/DataTable";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

import { FeedDefinitionDrawer } from "./FeedDefinitionDrawer";
import { FeedDefinitionsPanel } from "./FeedDefinitionsPanel";
import {
  useDqEvents,
  useFeedDefinitions,
  usePatchThreshold,
  useRuns,
  useThresholds,
  useTriggerRun,
  type DqEventOut,
  type RunLogOut,
  type ThresholdOut,
} from "../api";

/** Global knobs: per-source thresholds + budgets (write), calibration
 *  suggestions (read from threshold notes), run log, data-quality events
 *  (arrival spikes — the old ALERT_HIGH class, informational by design),
 *  and the manual run trigger (admin). Deep-review USD cap arrives with
 *  step 5's AI panel. */
export function AdminTab() {
  const thresholds = useThresholds();
  const patch = usePatchThreshold();
  const runs = useRuns();
  const dq = useDqEvents();
  const trigger = useTriggerRun();
  const [budgetDraft, setBudgetDraft] = useState<Record<string, string>>({});
  // The definition drawer: keep only the id — edits invalidate the list
  // query, and resolving the row from it keeps the drawer live-fresh.
  const defs = useFeedDefinitions();
  const [defId, setDefId] = useState<string | null>(null);
  const [defExpanded, setDefExpanded] = useState(false);
  const selectedDef = defs.data?.find((d) => d.id === defId) ?? null;

  const thresholdColumns: DataTableColumn<ThresholdOut>[] = [
    { key: "level", header: "Level" },
    { key: "key_path", header: "Key" },
    {
      key: "z_threshold",
      header: "z",
      numeric: true,
      render: (r) => r.z_threshold ?? "3.5 (default)",
    },
    {
      key: "materiality_floor",
      header: "Materiality",
      numeric: true,
      render: (r) => r.materiality_floor ?? "default",
    },
    {
      key: "budget_per_quarter",
      header: "Budget /qtr",
      render: (r) => (
        <div className="flex items-center gap-1.5">
          <Input
            className="h-7 w-16 text-xs"
            value={budgetDraft[r.id] ?? String(r.budget_per_quarter ?? 4)}
            onChange={(e) => setBudgetDraft((d) => ({ ...d, [r.id]: e.target.value }))}
          />
          <Button
            variant="outline"
            size="sm"
            disabled={patch.isPending}
            onClick={() => {
              const v = Number(budgetDraft[r.id]);
              if (!Number.isFinite(v) || v < 0) return;
              patch.mutate(
                { id: r.id, budget_per_quarter: v },
                { onSuccess: () => toast.success("Budget saved") },
              );
            }}
          >
            Save
          </Button>
        </div>
      ),
    },
    {
      key: "auto_tuned",
      header: "Tuned by",
      render: (r) => (r.auto_tuned ? "calibrator" : "human"),
    },
    {
      key: "note",
      header: "Note / calibration suggestions",
      render: (r) => (
        <span className="block max-w-96 whitespace-pre-line text-xs text-muted-foreground">
          {r.note ?? "—"}
        </span>
      ),
    },
  ];

  const runColumns: DataTableColumn<RunLogOut>[] = [
    { key: "run_date", header: "Run date" },
    { key: "kind", header: "Kind" },
    {
      key: "status",
      header: "Status",
      render: (r) => <StatusBadge value={r.status ?? "running"} />,
    },
    {
      key: "duration_ms",
      header: "Duration",
      numeric: true,
      render: (r) => (r.duration_ms != null ? `${(r.duration_ms / 1000).toFixed(1)}s` : "—"),
    },
    {
      key: "counts",
      header: "Counts",
      render: (r) => (
        <span className="text-xs text-muted-foreground">
          {["entities", "days_scored", "incidents_open", "first_seen"]
            .map((k) => `${k}: ${String(r.counts[k] ?? 0)}`)
            .join(" · ")}
        </span>
      ),
    },
  ];

  const dqColumns: DataTableColumn<DqEventOut>[] = [
    { key: "event_date", header: "Day", render: (r) => r.event_date ?? "—" },
    { key: "entity_source", header: "Source" },
    { key: "kind", header: "Kind", render: (r) => <StatusBadge value={r.kind} /> },
    {
      key: "magnitude_x",
      header: "Magnitude",
      numeric: true,
      render: (r) => (r.magnitude_x != null ? `${Math.round(r.magnitude_x)}×` : "—"),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <Card className="flex items-center gap-3 p-3">
        <div className="flex-1 text-sm text-muted-foreground">
          Manual detection run — scores every matured-but-unscored day (nightly job runs
          04:00 MT). Progress lands in the run log below.
        </div>
        <Button
          disabled={trigger.isPending}
          onClick={() =>
            trigger.mutate(
              { kind: "nightly" },
              { onSuccess: () => toast.success("Run scheduled — watch the run log") },
            )
          }
        >
          Run now
        </Button>
      </Card>

      <FeedDefinitionsPanel onOpen={(d) => setDefId(d.id)} />
      <FeedDefinitionDrawer
        definition={selectedDef}
        expanded={defExpanded}
        onExpandedChange={setDefExpanded}
        onClose={() => {
          setDefId(null);
          setDefExpanded(false);
        }}
      />

      <div>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Per-source thresholds &amp; budgets
        </h2>
        <DataTable
          columns={thresholdColumns}
          rows={thresholds.data ?? []}
          rowKey={(r) => r.id}
          loading={thresholds.isLoading}
          emptyMessage="No overrides — every source runs the feed defaults (z 3.5, floors 250/100/50, budget 4/qtr). Calibration suggestions appear here after the monthly replay."
        />
      </div>

      <div>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Run log
        </h2>
        <DataTable
          columns={runColumns}
          rows={runs.data ?? []}
          rowKey={(r) => r.id}
          loading={runs.isLoading}
          emptyMessage="No runs yet."
        />
      </div>

      <div>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Data-quality events — arrival spikes, never incidents
        </h2>
        <DataTable
          columns={dqColumns}
          rows={dq.data ?? []}
          rowKey={(r) => r.id}
          loading={dq.isLoading}
          emptyMessage="No arrival anomalies recorded."
        />
      </div>
    </div>
  );
}
